/* Prime Lending USA — motion system
   Vocabulary: typewriter headlines, count-ups, timer readout, print-slide reveals.
   Pure CSS + vanilla JS (no animation library). Everything respects
   prefers-reduced-motion and degrades to fully visible static content. */

(function () {
  "use strict";

  var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ---------- Header shrink ---------- */

  function initHeader() {
    var header = document.getElementById("SiteHeader");
    if (!header) return;

    var onScroll = function () {
      if (window.scrollY > 40) {
        header.classList.add("is-shrunk");
      } else {
        header.classList.remove("is-shrunk");
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
  }

  /* ---------- Mobile nav ---------- */

  function initMobileNav() {
    var toggle = document.querySelector("[data-nav-toggle]");
    var panel = document.querySelector("[data-nav-panel]");
    if (!toggle || !panel) return;

    toggle.addEventListener("click", function () {
      var open = toggle.getAttribute("aria-expanded") === "true";
      toggle.setAttribute("aria-expanded", open ? "false" : "true");
      panel.classList.toggle("is-open", !open);
    });
  }

  /* ---------- Reveal on scroll ---------- */

  function initReveals() {
    var targets = document.querySelectorAll(".reveal, .rule-reveal, .steps__item, .hero__visual");
    if (!targets.length) return;

    if (reduceMotion || !("IntersectionObserver" in window)) {
      targets.forEach(function (el) {
        el.classList.add("is-visible");
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.1 }
    );

    targets.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ---------- Typewriter headline ---------- */

  function initTypewriter() {
    var el = document.querySelector("[data-typewriter]");
    if (!el) return;

    var caret = el.parentElement
      ? el.parentElement.querySelector(".hero__caret")
      : null;

    var finish = function () {
      if (caret) {
        window.setTimeout(function () {
          caret.classList.add("is-done");
        }, 2600);
      }
    };

    if (reduceMotion) {
      finish();
      return;
    }

    var fullText = el.textContent;
    el.textContent = "";
    el.setAttribute("aria-label", fullText);

    var index = 0;

    var type = function () {
      if (index >= fullText.length) {
        finish();
        return;
      }
      el.textContent += fullText.charAt(index);
      index += 1;
      var delay = 34 + Math.random() * 42;
      if (fullText.charAt(index - 1) === " ") delay += 30;
      window.setTimeout(type, delay);
    };

    window.setTimeout(type, 350);
  }

  /* ---------- Count-ups (money) ---------- */

  function formatMoney(value) {
    return "$" + Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  function animateValue(el, target, duration, formatter) {
    if (reduceMotion) {
      el.textContent = formatter(target);
      return;
    }

    var start = null;

    var frame = function (timestamp) {
      if (start === null) start = timestamp;
      var progress = Math.min((timestamp - start) / duration, 1);
      var eased = 1 - Math.pow(1 - progress, 3);
      el.textContent = formatter(target * eased);
      if (progress < 1) {
        window.requestAnimationFrame(frame);
      }
    };

    window.requestAnimationFrame(frame);
  }

  function initCountUps() {
    var counters = document.querySelectorAll("[data-count]");
    if (!counters.length) return;

    var run = function (el) {
      var target = parseFloat(el.getAttribute("data-count"));
      if (isNaN(target)) return;
      animateValue(el, target, 1400, formatMoney);
    };

    if (reduceMotion || !("IntersectionObserver" in window)) {
      counters.forEach(run);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            run(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.4 }
    );

    counters.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ---------- Timer readout (m:ss count-up) ---------- */

  function formatClock(totalSeconds) {
    var seconds = Math.round(totalSeconds);
    var minutes = Math.floor(seconds / 60);
    var rest = seconds % 60;
    return minutes + ":" + (rest < 10 ? "0" : "") + rest;
  }

  function initTimers() {
    var timers = document.querySelectorAll("[data-timer]");
    if (!timers.length) return;

    var run = function (el) {
      var target = parseFloat(el.getAttribute("data-timer"));
      if (isNaN(target)) return;
      animateValue(el, target, 1700, formatClock);
    };

    if (reduceMotion || !("IntersectionObserver" in window)) {
      timers.forEach(run);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            run(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.4 }
    );

    timers.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ---------- FAQ accordion ---------- */

  function initFaq() {
    var toggles = document.querySelectorAll("[data-faq-toggle]");
    toggles.forEach(function (toggle) {
      toggle.addEventListener("click", function () {
        var expanded = toggle.getAttribute("aria-expanded") === "true";
        var panelId = toggle.getAttribute("aria-controls");
        var panel = panelId ? document.getElementById(panelId) : null;

        toggle.setAttribute("aria-expanded", expanded ? "false" : "true");
        if (panel) {
          if (expanded) {
            panel.setAttribute("hidden", "");
          } else {
            panel.removeAttribute("hidden");
          }
        }
      });
    });
  }

  /* ---------- Init ---------- */

  function init() {
    initHeader();
    initMobileNav();
    initReveals();
    initTypewriter();
    initCountUps();
    initTimers();
    initFaq();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
